import type { RouteLocationNormalized } from 'vue-router';
import { useAnalyticsStore } from '@/stores/index';
import { useMapStoreData } from '@/stores/data';

export type ModuleClickPayload = {
	/** Stable rollup key: config `id`, or built-in name (`map`, `scenarios`), or `{module}-{index}`. */
	key?: string;
	module: string;
	index?: number;
	id?: string;
	title?: string;
	type?: string;
	route?: string;
	source?: 'router' | 'home';
};

/** Preferred session rollup key — matches options.modules `id` when set. */
export function resolveModuleKey(payload: ModuleClickPayload): string {
	if (payload.key) return payload.key;
	if (payload.id) return String(payload.id);
	if (payload.module === 'map' || payload.module === 'scenarios') return payload.module;
	if (payload.index !== undefined && !Number.isNaN(payload.index)) {
		return `${payload.module}-${payload.index}`;
	}
	return payload.module;
}

export function trackModuleClick(payload: ModuleClickPayload) {
	const key = resolveModuleKey(payload);
	useAnalyticsStore().send('module_click', { ...payload, key });
}

/** Resolve a configured route module (video, gallery, scenario, …) by routes[] index. */
function moduleFromRouteIndex(routeName: string, index: number, path: string) {
	const mod = useMapStoreData().routes[index];
	trackModuleClick({
		module: routeName,
		index,
		id: mod?.id,
		title: mod?.title,
		type: mod?.type,
		route: path,
		source: 'router',
	});
}

/**
 * Called from vue-router afterEach when a module view loads.
 * Skips home; map/scenarios/video/etc. are resolved from route name + params.id.
 */
export function trackModuleFromRoute(to: RouteLocationNormalized) {
	const path = to.fullPath;
	const name = String(to.name ?? '');

	if (name === 'home') return;

	if (name === 'map') {
		trackModuleClick({ module: 'map', key: 'map', title: 'landing_page.map.title', route: path, source: 'router' });
		return;
	}
	if (name === 'scenarios') {
		trackModuleClick({
			module: 'scenarios',
			key: 'scenarios',
			title: 'landing_page.scenarios.title',
			route: path,
			source: 'router',
		});
		return;
	}
	if (name === 'wishingWell') {
		trackModuleClick({ module: 'wishingWell', route: path, source: 'router' });
		return;
	}
	if (name === 'eventList') {
		trackModuleClick({ module: 'eventList', route: path, source: 'router' });
		return;
	}

	const idParam = to.params.id;
	const index = idParam !== undefined && idParam !== '' ? Number(idParam) : NaN;

	const indexedRoutes = ['video', 'gallery', 'iframe', 'text', 'scenario', 'bats', 'pedestrianCounter', 'eventListModule'];
	if (indexedRoutes.includes(name) && !Number.isNaN(index)) {
		moduleFromRouteIndex(name, index, path);
		return;
	}

	if (indexedRoutes.includes(name)) {
		trackModuleClick({ module: name, route: path, source: 'router' });
	}
}

/** Map / mapCopy tiles open via showMap overlay — no route change; track from HomeView. */
export function trackMapModuleFromHome(kind: 'map' | 'mapCopy', item?: Record<string, unknown>, index?: number) {
	if (kind === 'map') {
		trackModuleClick({ module: 'map', key: 'map', title: 'landing_page.map.title', source: 'home' });
		return;
	}
	const moduleKey = (item?.id as string) ?? `${(item?.title as string) ?? 'module'}-${index}`;
	trackModuleClick({
		module: 'mapCopy',
		key: moduleKey,
		index,
		id: moduleKey,
		title: item?.title as string | undefined,
		type: (item?.type as string) ?? 'mapCopy',
		source: 'home',
	});
}
